'use client';

import { Accordion, AccordionDetails, AccordionSummary, alpha, Box, Typography } from "@mui/material";
import useIsDesktop from "@/hooks/useDesktop";
import { ExpandMore } from "@mui/icons-material";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { ImageMetaOverlayViewOptions } from "@/app/features/shared/media-view/types/types";
import { mediaRefCaption, mediaRefName } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import { useId, useMemo, useState } from "react";

interface ImageMetaProps {
    src: MediaRef;
    options: ImageMetaOverlayViewOptions;
}

export default function MetaOverlay({
    src,
    options,
}: ImageMetaProps) {

    const isDesktop = useIsDesktop();
    const name = useMemo(() => mediaRefName(src), [src]);
    const caption = useMemo(() => mediaRefCaption(src), [src]);
    const accordionSummaryId = useId();
    const accordionDetailsId = useId();

    const [isExpanded, setIsExpanded] = useState<boolean>(isDesktop);

    const shouldShowName = Boolean(options.showName && name);
    const shouldShowCaption = Boolean(options.showCaption && caption);

    if (!shouldShowName && !shouldShowCaption) {
        return null;
    }

    const titleView = (
        <Box
            sx={(theme) => ({
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.background.default, .9),
                p: 2,
                zIndex: 200,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
            })}
        >
            {shouldShowName && (
                <Typography
                    variant="h3"
                    color="text.primary"
                    textAlign="center"
                    sx={{
                        mb: 0,
                        overflow: 'hidden',
                        textWrapMode: 'nowrap',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {name}
                </Typography>
            )}
        </Box>
    );

    switch (options.variant ?? 'expandable') {
        case 'titleOnly':
            return titleView;
        case 'expandable':
            if (!shouldShowCaption) return titleView;

            return (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                    }}
                >
                    <Accordion
                        expanded={isExpanded}
                        onClick={() => setIsExpanded(prev => !prev)}
                        sx={{
                            backgroundColor: (theme) => alpha(theme.palette.background.default, .9),
                            '&.MuiAccordion-rounded': {
                                borderRadius: 0,
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                            },
                        }}
                    >
                        {shouldShowName && (
                            <AccordionSummary
                                expandIcon={<ExpandMore sx={{ fontSize: 32 }} color="primary" />}
                                aria-controls={accordionDetailsId}
                                id={accordionSummaryId}
                            >
                                <Typography variant="h3" textAlign="center" sx={{ mb: 0 }}>{name}</Typography>
                            </AccordionSummary>
                        )}
                        {shouldShowCaption && (
                            <AccordionDetails id={accordionDetailsId} aria-labelledby={accordionSummaryId}>
                                <Typography variant="body1" textAlign="start">{caption}</Typography>
                            </AccordionDetails>
                        )}
                    </Accordion>
                </Box>
            )
    }
}
